/* Yatas Bedding NL — shared carousel.
 *
 * One implementation backing the hero slider, banner rails, category rails and
 * product rails. Configured from data attributes so each section can mirror the
 * reference site's own slider settings (slides per view, autoplay, fade, loop).
 *
 * Markup contract:
 *   <ybn-carousel data-per-view="4" data-per-view-mobile="1.4" ...>
 *     <div data-track>  <div data-slide>…</div> … </div>
 *     <button data-prev> <button data-next>
 *     <div data-dots></div>
 *   </ybn-carousel>
 */
(function () {
  'use strict';

  class YbnCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.allSlides = Array.from(this.track.querySelectorAll('[data-slide]'));
      if (!this.allSlides.length) return;
      this.slides = this.visibleSlides();
      if (!this.slides.length) return;

      this.fade = this.dataset.effect === 'fade';
      this.loop = this.dataset.loop === 'true';
      this.index = 0;

      this.applyPerView();
      this.wireControls();
      this.buildDots();
      this.startAutoplay();

      this._wasMobile = this.isMobile();
      this._onResize = () => this.handleBreakpoint();
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    // A slide can opt out of one viewport with data-hide-mobile or
    // data-hide-desktop. It stays in the DOM and CSS hides it, but it also has to
    // leave this.slides, or the dots would number a slide that can never be
    // reached and autoplay would land on a blank frame.
    visibleSlides() {
      const mobile = this.isMobile();
      return this.allSlides.filter((s) =>
        mobile ? !s.hasAttribute('data-hide-mobile') : !s.hasAttribute('data-hide-desktop')
      );
    }

    // Crossing the breakpoint changes which slides exist, so the dots have to be
    // rebuilt. Plain applyPerView would leave the old count behind.
    handleBreakpoint() {
      const mobile = this.isMobile();
      if (mobile === this._wasMobile) {
        this.applyPerView();
        return;
      }
      this._wasMobile = mobile;
      // a slide dropped from the list could otherwise keep is-active and stay
      // painted over the top in fade mode
      this.allSlides.forEach((s) => s.classList.remove('is-active'));
      this.slides = this.visibleSlides();
      if (!this.slides.length) return;
      this.index = Math.min(this.index, this.slides.length - 1);
      this.buildDots();
      this.goto(this.index, false);
      this.restartAutoplay();
    }

    get perView() {
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const raw = mobile ? this.dataset.perViewMobile : this.dataset.perView;
      const n = parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - Math.floor(this.perView));
    }

    applyPerView() {
      if (this.fade) {
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
        return;
      }
      this.style.setProperty('--ybn-per-view', String(this.perView));
      this.goto(Math.min(this.index, this.maxIndex), false);
    }

    wireControls() {
      this.querySelector('[data-prev]')?.addEventListener('click', () => {
        this.goto(this.index - 1);
        this.restartAutoplay();
      });
      this.querySelector('[data-next]')?.addEventListener('click', () => {
        this.goto(this.index + 1);
        this.restartAutoplay();
      });

      let startX = null;
      this.track.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );
      this.track.addEventListener(
        'touchend',
        (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) {
            this.goto(this.index + (dx < 0 ? 1 : -1));
            this.restartAutoplay();
          }
          startX = null;
        },
        { passive: true }
      );
    }

    buildDots() {
      const host = this.querySelector('[data-dots]');
      if (!host) return;

      const count = this.fade ? this.slides.length : this.maxIndex + 1;
      host.innerHTML = '';
      this.dots = [];

      // The reference hero numbers its bullets 1..n instead of using plain dots.
      const numbered = this.dataset.dotsStyle === 'numbered';
      if (numbered) host.classList.add('is-numbered');

      // The reference's active bullet carries a ring that sweeps round as
      // autoplay counts down; its duration is the autoplay delay.
      const delay = parseInt(this.dataset.autoplay, 10);
      if (numbered && Number.isFinite(delay) && delay > 0) {
        host.style.setProperty('--ybn-dot-duration', `${delay}ms`);
      }

      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ybn-carousel__dot';
        if (numbered) {
          b.innerHTML =
            '<span class="ybn-carousel__progress" aria-hidden="true">' +
            '<svg viewBox="0 0 48 48">' +
            '<circle cx="24" cy="24" r="23" class="progress-bg"></circle>' +
            '<circle cx="24" cy="24" r="23"></circle>' +
            '</svg></span>';
          b.append(String(i + 1));
        }
        b.setAttribute('aria-label', `Ga naar slide ${i + 1}`);
        b.addEventListener('click', () => {
          this.goto(i);
          this.restartAutoplay();
        });
        host.append(b);
        this.dots.push(b);
      }
      this.syncDots();
    }

    syncDots() {
      this.dots?.forEach((d, i) => {
        const active = i === this.index;
        d.classList.toggle('is-active', active);
        // Re-adding the class is not enough to replay a CSS animation on an
        // element that never left the DOM - the node has to be swapped out.
        if (active) {
          const ring = d.querySelector('.ybn-carousel__progress svg');
          if (ring) ring.replaceWith(ring.cloneNode(true));
        }
      });
    }

    goto(i, animate = true) {
      const max = this.fade ? this.slides.length - 1 : this.maxIndex;

      if (this.loop) {
        this.index = ((i % (max + 1)) + max + 1) % (max + 1);
      } else {
        this.index = Math.max(0, Math.min(i, max));
      }

      if (this.fade) {
        this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
      } else {
        this.track.style.transition = animate ? '' : 'none';
        this.track.style.transform = `translateX(-${(this.index * 100) / this.perView}%)`;
        if (!animate) {
          // Force a reflow so the suppressed transition doesn't leak into the
          // next programmatic move.
          void this.track.offsetHeight;
          this.track.style.transition = '';
        }
      }

      this.syncDots();
      this.toggleArrows(max);
    }

    toggleArrows(max) {
      if (this.loop) return;
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.disabled = this.index <= 0;
      if (next) next.disabled = this.index >= max;
    }

    startAutoplay() {
      const delay = parseInt(this.dataset.autoplay, 10);
      if (!Number.isFinite(delay) || delay <= 0) return;

      // Clear first: mouseleave calls back in here, and without this every
      // hover left another interval running - the slider sped up each time.
      this.stopAutoplay();

      this._timer = setInterval(() => {
        const max = this.fade ? this.slides.length - 1 : this.maxIndex;
        this.goto(this.index >= max ? 0 : this.index + 1);
      }, delay);

      this.classList.remove('is-paused');

      // Bound once, for the same reason.
      if (!this._hoverBound) {
        this._hoverBound = true;
        this.addEventListener('mouseenter', () => {
          this.classList.add('is-paused');
          this.stopAutoplay();
        });
        this.addEventListener('mouseleave', () => this.startAutoplay());
      }
    }

    stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  if (!customElements.get('ybn-carousel')) {
    customElements.define('ybn-carousel', YbnCarousel);
  }
})();
